import * as db from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { chunk } from 'lodash';

import { formatIf } from '@helpers/Formatters';
import { allFulfilled } from '@helpers/Promises';
import { firestore } from '@helpers/firebase/Initialize';

export default db;

export const FIRESTORE_IN_QUERY_LIMIT = 10;

export async function addDocWithTimestamps(ref: db.CollectionReference, data: Record<string, any>) {
  return db.addDoc(ref, {
    ...data,
    createdAt: db.serverTimestamp(),
    updatedAt: db.serverTimestamp(),
  });
}

export async function setDocWithTimestamps(ref: db.DocumentReference, data: Record<string, any>) {
  return db.setDoc(ref, {
    ...data,
    createdAt: db.serverTimestamp(),
    updatedAt: db.serverTimestamp(),
  });
}

export async function updateDocWithTimestamps(
  ref: db.DocumentReference,
  data: Record<string, any>
) {
  return db.updateDoc(ref, { ...data, updatedAt: db.serverTimestamp() });
}

export async function getDocsIn<T>(
  collection: string,
  fieldPath: string | db.FieldPath,
  opStr: db.WhereFilterOp,
  items: T[],
  queryConstraints: db.QueryConstraint[] = []
) {
  const chunks = chunk(items, FIRESTORE_IN_QUERY_LIMIT);
  const promises: Promise<db.QuerySnapshot<db.DocumentData>>[] = [];
  for (const chunk of chunks) {
    const q = db.query(
      db.collection(firestore, collection),
      db.where(fieldPath, opStr, chunk),
      ...queryConstraints
    );
    promises.push(db.getDocs(q));
  }
  const snapshots = await allFulfilled(promises);
  const docs: db.QueryDocumentSnapshot<db.DocumentData>[] = [];
  for (const snapshot of snapshots) {
    docs.push(...snapshot.docs);
  }
  return docs;
}

export function convertFirestoreData(data?: db.DocumentData) {
  if (!data) return {};

  return formatIf(
    data,
    (elem: any) => elem instanceof Timestamp,
    (elem: Timestamp) => elem.toDate()
  );
}

export function parseFirestoreDoc<T extends object>(
  doc: db.DocumentSnapshot,
  idField: keyof T & string
): T {
  return {
    ...convertFirestoreData(doc.data()),
    [idField]: doc.id,
  } as T;
}
